<template>
  <section v-if="content">
    <HomeHero :content="heroBanner" />
    <KspBlockContent
      :content="kspSection"
      class="ksp"
    />
    <CuratedSection :content="curatedSection" />
    <RegionBrands
      v-if="regionBrandContent"
      :content="regionBrandContent"
    />
    <BrandLogoCarousel :content="brandLogoCarouselContent" />
    <HomeTestimonials
      v-if="homeTestimonialsContent"
      :content="homeTestimonialsContent"
    />
    <SocialProof
      class="social-proof"
      :content="socialProofContent"
    />
    <FeaturedBrands :e-title="featuredBrandsTitle" />
    <AnkorstartBanner :content="ankorstartBannerContent" />
    <RetailerFaq
      v-if="faqContent"
      class="faq"
      :content="faqContent"
    />
    <TrustArea :content="trustArea" />
  </section>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import CuratedSection from '@monolith/legacy/components/home/revamp/curated-section.vue';
import RegionBrands from '@monolith/legacy/components/home/region-brands.vue';
import HomeTestimonials from '@monolith/legacy/components/home/home-testimonials.vue';
import HomeHero from '@monolith/legacy/components/home-hero/home-hero.vue';
import SocialProof, { SocialProofContent, SocialProofItem } from '@monolith/legacy/components/home/social-proof.vue';
import FeaturedBrands from '@monolith/legacy/components/home/featured-brands.vue';
import AnkorstartBanner from '@monolith/legacy/components/home/revamp/ankorstart-banner.vue';
import RetailerFaq from '@monolith/legacy/components/retailer-faq.vue';
import TrustArea from '@monolith/legacy/components/home/revamp/trust-area.vue';

import { HomeContent, CuratedSectionContent, RegionBrandContent, TrustAreaContent, HomeBannerContent } from '@monolith/legacy/types/home/home';
import KspBlockContent from '@monolith/legacy/components/my-shop/ksp-block/ksp-block-content.vue';
import BrandLogoCarousel, { BrandLogoContent } from '@monolith/legacy/components/home/brand-logo-carousel.vue';
import { PrismicTestimonials } from '@monolith/legacy/types/prismic/home-testimonials';
import { FaqInterface } from '@monolith/legacy/types/faq';

const props = defineProps<{
  content: HomeContent | null;
}>();

const content = computed(() => props.content);


const curatedSection = computed((): CuratedSectionContent => {
  return {
    title: content.value?.g_title[0].text,
    description: content.value?.g_description,
    name: content.value?.g_name[0].text,
    logo: content.value?.g_logo,
    firstImage: content.value?.g_image_1,
    secondImage: content.value?.g_image_2,
    cta: content.value?.g_cta,
    cta_link: content.value?.g_cta_link,
  };
});

const kspSection = computed(() => {
  return content.value?.keys;
});

const brandLogoCarouselContent = computed((): BrandLogoContent => {
  return {
    images: content.value?.i_images.map((image) => {
      return {
        brand_link: image.i_brand_link,
        brand_logo: image.i_brand_logo,
      };
    }),
  };
});

const regionBrandContent = computed((): RegionBrandContent => {
  return {
    country: content.value?.h_country,
    other_country: content.value?.h_other_country,
    other_country_title_override: content.value?.h_other_country_title_override,
    title: content.value?.h_title,
  };
});

const homeTestimonialsContent = computed((): PrismicTestimonials[] => {
  return content.value?.j_testimonial.map((testimonials) => {
    return {
      quote: testimonials.j_quote,
      author_name: testimonials.j_author_name,
      author_subtitle: testimonials.j_author_subtitle,
    };
  });
});

const socialProofContent = computed((): SocialProofContent => {
  return {
    title: content.value?.l_title_social,
    items: content.value?.l_items.map((item): SocialProofItem => {
      return {
        href: item.l_href,
        image: item.l_image,
        description: item.l_description,
      };
    }),
  };
});

const ankorstartBannerContent = computed(() => {
  return {
    background: content.value?.k_background,
    background_mobile: content.value?.k_background_mobile,
    ctalabel: content.value?.k_ctalabel,
    link: content.value?.k_link,
    subtitle: content.value?.k_subtitle,
    title: content.value?.k_title,
  };
});

const faqContent = computed((): FaqInterface => {
  return {
    faq_link: content.value?.faq_link,
    overridetitle: content.value?.overridetitle,
    questions: content.value?.questions,
  };
});

const trustArea = computed((): TrustAreaContent => {
  return {
    title: content.value?.n_title,
    trustpilot: content.value?.n_trustpilot,
    google: content.value?.n_google,
  };
});

const heroBanner = computed((): HomeBannerContent => {
  return {
    cta_url: content.value?.a_hero_url,
    background_image: content.value?.a_background_image,
    foreground_image: content.value?.a_foreground_image,
  };
});

const featuredBrandsTitle = computed((): string => {
  return content.value?.e_title ?? '';
});
</script>
<style lang="scss" scoped>
.social-proof {
  @apply ds-mt-4;
}

.ksp {
  @apply ds-mt-6.5 ds-mb-2;
}

.faq {
  @apply ds-bg-neutral-300 ds-pt-1 ds-pb-1;
}
</style>
